/**
 * Shared utility for determining the correct apply route (V2 vs V1)
 * by calling the backend API and detecting the response type.
 *
 * This logic is used by:
 * - Team listing page Apply buttons (AgencyApplyCard)
 * - Property detail page Start Application button (property_info.jsx)
 *
 * The backend API at /sm/property/{id}/apply returns:
 * - V2 redirect info (with v2RedirectInfo.shouldRedirect and v2RedirectInfo.redirectURL) for V2-eligible properties
 * - V1 redirect or HTML for V1-only properties
 */

import { apiBaseUrl } from 'config/env'

import * as snugLogger from 'app/services/snugLogger'

/**
 * Determines the correct apply URL (V2 vs V1) by calling the backend API
 * @param {string} propertyId - The property/offer ID
 * @param {string} queryString - URL query parameters (should include leading '?')
 * @param {string} fallbackUrl - V1 fallback URL if V2 not available
 * @returns {Promise<string>} The URL to navigate to
 */
export const getApplyRouteUrl = (propertyId, queryString = '', fallbackUrl) => {
  const buildRedirectUrlWithQuery = (redirectUrl) => {
    if (!queryString) {
      return Promise.resolve(redirectUrl)
    }

    return Promise.resolve()
      .then(() => {
        const baseUrl = new URL(redirectUrl, window.location.origin)
        const incomingParams = new URLSearchParams(
          queryString.startsWith('?') ? queryString.slice(1) : queryString,
        )

        incomingParams.forEach((value, key) => {
          if (!baseUrl.searchParams.has(key)) {
            baseUrl.searchParams.set(key, value)
          }
        })

        return `${baseUrl.pathname}${baseUrl.search}`
      })
      .catch((err) => {
        snugLogger.error('Error merging apply route query params:', err)
        return redirectUrl
      })
  }

  // apiBaseUrl includes /v1/ suffix
  const targetUrl = `${apiBaseUrl}sm/property/${propertyId}/apply${queryString}`

  return fetch(targetUrl, {
    method: 'GET',
    credentials: 'include',
  })
    .then((response) => {
      const contentType = response.headers.get('content-type')

      if (contentType && contentType.includes('application/json')) {
        return response.json().then((data) => {
          // Check for V2 redirect info from backend (proper pattern)
          // Backend should return v2RedirectInfo.shouldRedirect and v2RedirectInfo.redirectURL
          // for applications that need to use the V2 UI
          if (
            data?.v2RedirectInfo?.shouldRedirect &&
            data?.v2RedirectInfo?.redirectURL
          ) {
            return buildRedirectUrlWithQuery(data.v2RedirectInfo.redirectURL)
          }

          // Check for explicit redirectUrl in response (fallback)
          if (data && data.redirectUrl) {
            return data.redirectUrl
          }

          // Fall back to V1 if V2 not detected
          return fallbackUrl
        })
      }

      // Fall back to V1 if V2 not detected
      return fallbackUrl
    })
    .catch((err) => {
      snugLogger.error('Error determining apply route:', err)
      // Fall back to V1 on any error
      return fallbackUrl
    })
}
